import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useAppDispatch } from '../store/hooks';
import { saveUserSession } from '../store/authSlice';
import AuthService from '../services/auth';
import InternationalPhoneInput from './InternationalPhoneInput';

interface CellphoneLoginScreenProps {
  onBack: () => void;
}

const CellphoneLoginScreen: React.FC<CellphoneLoginScreenProps> = ({ onBack }) => {
  const dispatch = useAppDispatch();
  const [cellphone, setCellphone] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // 验证手机号格式
  const validateCellphone = (phone: string): boolean => {
    // 检查是否包含国际区号和至少7位数字
    const phoneRegex = /^\+\d{1,4}\d{7,15}$/;
    return phoneRegex.test(phone);
  };

  // 手机号密码登录
  const handleLogin = async () => {
    if (!validateCellphone(cellphone)) {
      Alert.alert('错误', '请输入正确的手机号码');
      return;
    }

    if (!password.trim()) {
      Alert.alert('错误', '请输入密码');
      return;
    }

    if (password.length < 6) {
      Alert.alert('错误', '密码长度至少6位');
      return;
    }

    try {
      setLoading(true);
      const user = await AuthService.signInWithCellphone(cellphone, password);
      dispatch(saveUserSession(user));
      Alert.alert('成功', '登录成功！');
    } catch (error: any) {
      console.error('Cellphone login error:', error);
      Alert.alert('错误', error.message || '登录失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <View style={styles.content}>
        <TouchableOpacity style={styles.backButton} onPress={onBack}>
          <Text style={styles.backButtonText}>← 返回</Text>
        </TouchableOpacity>

        <Text style={styles.title}>手机号登录</Text>
        <Text style={styles.subtitle}>使用手机号和密码登录</Text>

        <View style={styles.inputContainer}>
          <Text style={styles.label}>手机号</Text>
          <InternationalPhoneInput
            value={cellphone}
            onChangeText={setCellphone}
            placeholder="请输入手机号"
          />
        </View>

        <View style={styles.inputContainer}>
          <Text style={styles.label}>密码</Text>
          <TextInput
            style={styles.input}
            placeholder="请输入密码（至少6位）"
            value={password}
            onChangeText={setPassword}
            secureTextEntry={true}
            autoCapitalize="none"
          />
        </View>

        <TouchableOpacity
          style={[styles.button, (!validateCellphone(cellphone) || password.length < 6 || loading) && styles.buttonDisabled]}
          onPress={handleLogin}
          disabled={!validateCellphone(cellphone) || password.length < 6 || loading}
        >
          <Text style={styles.buttonText}>
            {loading ? '登录中...' : '登录'}
          </Text>
        </TouchableOpacity>

        <Text style={styles.disclaimer}>
          未注册的手机号请先进行注册
        </Text>
      </View>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f0f0f0',
  },
  content: {
    flex: 1,
    padding: 20,
    justifyContent: 'center',
  },
  backButton: {
    position: 'absolute',
    top: 50,
    left: 20,
    zIndex: 1,
  },
  backButtonText: {
    fontSize: 16,
    color: '#4285F4',
    fontWeight: '600',
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#333',
    textAlign: 'center',
    marginBottom: 10,
  },
  subtitle: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginBottom: 40,
  },
  inputContainer: {
    marginBottom: 20,
  },
  label: {
    fontSize: 16,
    color: '#333',
    marginBottom: 8,
    fontWeight: '600',
  },
  input: {
    height: 50,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    paddingHorizontal: 15,
    fontSize: 16,
    backgroundColor: '#fff',
    color: '#333',
  },
  button: {
    backgroundColor: '#4285F4',
    height: 50,
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 15,
  },
  buttonDisabled: {
    backgroundColor: '#ccc',
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  disclaimer: {
    fontSize: 12,
    color: '#999',
    textAlign: 'center',
    marginTop: 20,
  },
});

export default CellphoneLoginScreen;
